/* WP-10 WebAuthn helpers: wrap @simplewebauthn/browser with the portal API
 * endpoints (login, registration, step-up). Verify calls return challengeId
 * + options; step-up sets stepUpAt on the session — callers then retry the
 * original request with the same Idempotency-Key (§14.7). */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { portalApi, PortalApiError } from '../api/portal-api';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

export function passkeySupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

/** Login: options → browser assertion → verify (sets the session cookie). */
export async function passkeySignIn(): Promise<void> {
  const { challengeId, options } = await portalApi.post<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/auth/passkeys/authentication/options', {});
  const assertion = await startAuthentication({ optionsJSON: options });
  await portalApi.post('/auth/passkeys/authentication/verify', { challengeId, assertion });
}

/** Register a new passkey on this device (authed or activation context). */
export async function passkeyRegister(deviceLabel?: string): Promise<void> {
  const { challengeId, options } = await portalApi.post<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }>('/auth/passkeys/registration/options', {});
  const attestation = await startRegistration({ optionsJSON: options });
  await portalApi.post('/auth/passkeys/registration/verify', {
    challengeId,
    attestation,
    deviceLabel,
  });
}

/** Step-up via passkey — sets stepUpAt on the session. */
export async function passkeyStepUp(): Promise<void> {
  const { challengeId, options } = await portalApi.post<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/auth/step-up/passkey/options', {});
  const assertion = await startAuthentication({ optionsJSON: options });
  await portalApi.post('/auth/step-up/passkey/verify', { challengeId, assertion });
}

export type OtpStepUpStart = {
  challengeId: string;
  channel: string;
  sentToMasked: string;
  expiresAt: string;
};

export async function otpStepUpStart(): Promise<OtpStepUpStart> {
  return portalApi.post<OtpStepUpStart>('/auth/step-up/otp/start', {});
}

export async function otpStepUpVerify(challengeId: string, otp: string): Promise<void> {
  await portalApi.post('/auth/step-up/otp/verify', { challengeId, otp });
}

/** Maps WebAuthn browser errors to a stable portal code. */
export function toPasskeyError(err: unknown): PortalApiError {
  if (err instanceof PortalApiError) return err;
  const name = (err as { name?: string } | null)?.name ?? '';
  if (name === 'NotAllowedError') {
    return new PortalApiError(400, 'PORTAL_PASSKEY_FAILED', { reason: 'cancelled' });
  }
  return new PortalApiError(400, 'PORTAL_PASSKEY_FAILED');
}
